import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';
import { FormatService } from '../../services/format/format.service';
import { TilawahComponent } from '../tilawah/tilawah.component';
import * as moment from 'moment';
import { targetLabel, turnTag, turnWord, periodUnit, scopeLabel, recordLabel, currentTurnLabel, TURN_TAG_SOURCE } from '../../services/tilawah-period';

// Read-only detail of one tilawah group: header + lazy tabs. No mutation of
// any kind lives here — see TILAWAH_MONITORING_PLAN.md §0.
@Component({
  selector: 'app-tilawah-detail',
  templateUrl: './tilawah-detail.component.html',
  styleUrls: ['./tilawah-detail.component.css']
})
export class TilawahDetailComponent implements OnInit, OnDestroy {
  id : number = 0;
  group : any = null;
  isHeaderLoading : boolean = true;
  notFound : boolean = false;
  directoryFile : string = '';
  pageSize : number = 10;

  tab : string = 'members'; // members | tasks | progress | activity | announcements | khatam
  readonly tabs = [
    {key: 'members', label: 'Anggota'},
    {key: 'tasks', label: 'Tugas Bacaan'},
    {key: 'progress', label: 'Progres'},
    {key: 'announcements', label: 'Pengumuman'},
    {key: 'khatam', label: 'Riwayat Khatam'},
    {key: 'activity', label: 'Aktivitas'},
    {key: 'chats', label: 'Percakapan'},
  ];
  loading : any = {}; // per tab

  // members
  members : any = [];
  memberFilter : string = '';

  // tasks
  taskCycle : number = 0;
  tasks : any = [];
  tasksTotal : number = 0;
  tasksPage : number = 1;
  tasksPages : any = [];
  taskOpen : any = {};

  // progress
  progressCycle : number = 0;
  progress : any = null;

  // activity
  act : any = [];
  actTotal : number = 0;
  actPage : number = 1;
  actPages : any = [];
  actKind : string = ''; // the activity tab is system events only; conversation lives in its own tab

  // conversation (oldest → newest in the array; older pages are prepended)
  chats : any = [];
  chatsTotal : number = 0;
  chatsLoading : boolean = false;
  chatsOlderLoading : boolean = false;
  threads : any = {}; // chatId -> {open, loading, rows}

  // announcements
  ann : any = [];
  annTotal : number = 0;
  annPage : number = 1;
  annPages : any = [];

  // khatam history
  khatam : any = [];

  isViewer : string = '';
  private routeSub : Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    moment.locale('id');
    this.pageSize = this.apiService.LIMIT || 10;
    this.routeSub = this.route.paramMap.subscribe(params => {
      this.id = Number(params.get('id')) || 0;
      this.resetAll();
      this.loadHeader();
    });
  }
  ngOnDestroy(): void {
    if(this.routeSub){ this.routeSub.unsubscribe(); }
    document.body.style.overflow = '';
  }

  private resetAll(){
    this.group = null; this.notFound = false; this.isHeaderLoading = true;
    this.members = []; this.tasks = []; this.tasksTotal = 0; this.tasksPage = 1; this.taskOpen = {};
    this.progress = null; this.act = []; this.actTotal = 0; this.actPage = 1;
    this.chats = []; this.chatsTotal = 0; this.threads = {};
    this.ann = []; this.annTotal = 0; this.annPage = 1; this.khatam = [];
    this.loaded = {};
  }

  // ===== header =====
  loadHeader(){
    this.apiService.methodGetSilent(`tilawah/detail/${this.id}`, this.authService.getToken()).subscribe((response) => {
      this.isHeaderLoading = false;
      if(response && response.status == 200 && response.data){
        this.directoryFile = response.directoryFile || '';
        const g = response.data;
        g.photoUrl = this.photoUrl(g.photo, 'tilawah/');
        g.ownerPhotoUrl = this.photoUrl(g.owner_photo, 'user/avatar/');
        g.createdVw = g.created_at ? moment(g.created_at).format('D MMMM YYYY') : '-';
        g.deletedVw = g.deleted_at ? moment(g.deleted_at).format('D MMMM YYYY HH:mm') : '';
        g.lastActivityVw = g.last_activity_at ? moment(g.last_activity_at).fromNow() : 'Belum ada';
        g.targetVw = targetLabel(g, (n) => this.formatNumber(n));
        g.scopeVw = scopeLabel(g.reading_scope);
        g.recordVw = recordLabel(g.reading_record);
        g.turnWord = turnWord(g.reading_period);
        g.periodUnit = periodUnit(g.reading_period);
        g.currentTurnVw = currentTurnLabel(g.reading_period);
        this.group = g;
        this.taskCycle = g.current_cycle;
        this.progressCycle = g.current_cycle;
        this.openTab(this.tab, true);
      }else{
        this.notFound = true;
        if(response && response.message){ this.toastr.error(response.message, 'Gagal memuat grup'); }
      }
    }, () => { this.isHeaderLoading = false; this.notFound = true; });
  }

  photoUrl(photo : any, folder : string){
    if(!photo){ return ''; }
    return String(photo).includes('http') ? photo : `${this.directoryFile}${folder}${photo}`;
  }
  statusLabel(s : string){
    if(s === 'active'){ return 'Aktif'; }
    if(s === 'deleted'){ return 'Dihapus'; }
    return 'Senyap';
  }
  back(){ this.router.navigate(['/tilawah']); }

  // ===== tabs (lazy, cached until the route changes) =====
  private loaded : any = {};
  openTab(key : string, force : boolean = false){
    this.tab = key;
    // a tab click pins the tab bar right under the fixed topbar, so the chosen
    // tab and its content fill the screen (force = initial load, no scroll)
    if(!force){ this.scrollTabsToTop(); }
    if(this.loaded[key] && !force){ return; }
    this.loaded[key] = true;
    switch(key){
      case 'members': return this.loadMembers();
      case 'chats': return this.loadChats();
      case 'tasks': return this.loadTasks();
      case 'progress': return this.loadProgress();
      case 'activity': return this.loadActivity();
      case 'announcements': return this.loadAnnouncements();
      case 'khatam': return this.loadKhatam();
    }
  }

  // the card that holds the tab bar becomes the topmost thing on screen
  private scrollTabsToTop(){
    const tabs : any = document.querySelector('.td-tabs');
    if(!tabs){ return; }
    const card : any = tabs.closest('.card') || tabs;
    // only a fixed/sticky topbar covers the page; a scrolling one needs no offset
    const topbar : any = document.querySelector('.topbar');
    const pos = topbar ? getComputedStyle(topbar).position : '';
    const offset = ((pos === 'fixed' || pos === 'sticky') ? topbar.offsetHeight : 0) + 12;
    const top = card.getBoundingClientRect().top + window.pageYOffset - offset;
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth'});
  }

  // ----- members -----
  loadMembers(){
    this.loading.members = true;
    this.apiService.methodGetSilent(`tilawah-member/${this.id}`, this.authService.getToken()).subscribe((response) => {
      this.loading.members = false;
      if(response && response.status == 200){
        this.members = (response.data || []).map((m : any) => {
          m.photoUrl = this.photoUrl(m.photo, 'user/avatar/');
          m.nameVw = m.name || 'Akun tidak ditemukan';
          m.joinedVw = m.created_at ? moment(m.created_at).format('D MMM YYYY') : '-';
          m.lastDoneVw = m.last_done_at ? moment(m.last_done_at).fromNow() : '—';
          m.lastDoneFull = m.last_done_at ? moment(m.last_done_at).format('D MMMM YYYY HH:mm') : '';
          return m;
        });
      }
    }, () => { this.loading.members = false; });
  }
  get visibleMembers(){
    if(this.memberFilter === ''){ return this.members; }
    return this.members.filter((m : any) => String(m.status) === this.memberFilter);
  }
  memberCount(status : string){
    return status === '' ? this.members.length : this.members.filter((m : any) => String(m.status) === status).length;
  }
  memberStatusLabel(s : number){
    if(s == 1){ return 'Aktif'; }
    if(s == 0){ return 'Undangan tertunda'; }
    if(s == 3){ return 'Minta bergabung'; }
    return 'Dikeluarkan';
  }
  memberStatusClass(s : number){
    if(s == 1){ return 'on'; }
    if(s == 2){ return 'del'; }
    return 'wait';
  }
  roleLabel(m : any){
    if(m.is_owner){ return 'Pemilik'; }
    return m.role == 1 ? 'Admin' : 'Anggota';
  }

  // ----- tasks -----
  setTaskCycle(c : any){
    const cycle = Number(c);
    if(cycle === this.taskCycle){ return; }
    this.taskCycle = cycle; this.tasksPage = 1; this.taskOpen = {};
    this.loadTasks();
  }
  loadTasks(){
    this.loading.tasks = true;
    const offset = (this.tasksPage - 1) * this.pageSize;
    this.apiService.methodGetSilent(`tilawah/${this.id}/tasks/${this.taskCycle}/${offset}`, this.authService.getToken()).subscribe((response) => {
      this.loading.tasks = false;
      if(response && response.status == 200 && response.data){
        this.tasks = (response.data.tasks || []).map((t : any) => {
          t.dateVw = t.task_date ? moment(t.task_date).format('ddd, D MMM YYYY') : moment(t.created_at).format('ddd, D MMM YYYY');
          t.pct = t.parts_total ? Math.round(t.parts_done / t.parts_total * 100) : 0;
          t.isDone = t.parts_total > 0 && t.parts_done === t.parts_total;
          t.juzVw = t.begin_juz ? (t.begin_juz === t.end_juz ? `Juz ${t.begin_juz}` : `Juz ${t.begin_juz}–${t.end_juz}`) : '';
          (t.details || []).forEach((d : any) => {
            d.photoUrl = this.photoUrl(d.user_photo, 'user/avatar/');
            d.doneVw = d.status === 1 && d.updated_at ? moment(d.updated_at).format('D MMM HH:mm') : '';
          });
          return t;
        });
        this.tasksTotal = response.total || 0;
        this.tasksPages = this.pagesFor(this.tasksPage, this.pagesOf(this.tasksTotal));
      }
    }, () => { this.loading.tasks = false; });
  }
  toggleTask(t : any){ this.taskOpen[t.id] = !this.taskOpen[t.id]; }
  goTasksPage(p : any){
    if(p < 1 || p > this.pagesOf(this.tasksTotal) || p == this.tasksPage){ return; }
    this.tasksPage = p; this.taskOpen = {}; this.loadTasks();
  }

  // ----- progress -----
  setProgressCycle(c : any){
    const cycle = Number(c);
    if(cycle === this.progressCycle){ return; }
    this.progressCycle = cycle;
    this.loadProgress();
  }
  loadProgress(){
    this.loading.progress = true;
    this.apiService.methodGetSilent(`tilawah/${this.id}/progress/${this.progressCycle}`, this.authService.getToken()).subscribe((response) => {
      this.loading.progress = false;
      if(response && response.status == 200 && response.data){
        const p = response.data;
        (p.members || []).forEach((m : any) => {
          m.photoUrl = this.photoUrl(m.user_photo, 'user/avatar/');
          m.nameVw = m.user_name || 'Akun tidak ditemukan';
          m.lastDoneVw = m.last_done_at ? moment(m.last_done_at).fromNow() : '—';
        });
        p.completeCount = (p.juz || []).filter((j : any) => j.state === 'complete').length;
        p.partialCount = (p.juz || []).filter((j : any) => j.state === 'partial').length;
        this.progress = p;
      }
    }, () => { this.loading.progress = false; });
  }
  juzTitle(j : any){
    const label : any = {complete: 'tuntas dibaca bersama', partial: 'sedang dibaca', untouched: 'belum tersentuh', skipped: 'sebelum surat awal grup'};
    return `Juz ${j.juz} — ${label[j.state] || ''}`;
  }

  // ----- activity -----
  setActKind(kind : string){
    if(this.actKind === kind){ return; }
    this.actKind = kind; this.actPage = 1; this.loadActivity();
  }
  loadActivity(){
    this.loading.activity = true;
    const offset = (this.actPage - 1) * this.pageSize;
    const src = {systemOnly: '1', kind: this.actKind};
    this.apiService.methodGetSilent(`tilawah/${this.id}/activity/${offset}/${encodeURIComponent(JSON.stringify(src))}`, this.authService.getToken()).subscribe((response) => {
      this.loading.activity = false;
      if(response && response.status == 200){
        this.act = (response.data || []).map((a : any) => {
          a.meta = this.kindMeta(a.kind);
          a.timeVw = a.created_at ? moment(a.created_at).fromNow() : '';
          a.timeFull = a.created_at ? moment(a.created_at).format('D MMMM YYYY HH:mm') : '';
          a.userPhotoUrl = this.photoUrl(a.user_photo, 'user/avatar/');
          a.textVw = this.activityText(a);
          a.photoUrl = a.photo || '';
          return a;
        });
        this.actTotal = response.total || 0;
        this.actPages = this.pagesFor(this.actPage, this.pagesOf(this.actTotal));
      }
    }, () => { this.loading.activity = false; });
  }
  goActPage(p : any){
    if(p < 1 || p > this.pagesOf(this.actTotal) || p == this.actPage){ return; }
    this.actPage = p; this.loadActivity();
  }
  kindMeta(kind : string){
    return TilawahComponent.KIND_META[kind] || TilawahComponent.KIND_META.system;
  }
  kinds(){
    return Object.keys(TilawahComponent.KIND_META).filter(k => k !== 'message' && k !== 'system');
  }
  activityText(a : any){
    const who = a.user_name || 'Seseorang';
    switch(a.kind){
      case 'task_created':
        return a.task_day ? `${who} membuat tugas bacaan ${this.turnTag(a.task_day)}` : `${who} membuat tugas bacaan`;
      case 'announcement':
        return `Pengumuman: ${a.announcement_title || a.text || ''}`;
      case 'message':
        return a.text || (a.has_media ? '' : '');
      default:
        return a.text || '';
    }
  }

  // ===== super-admin deletions (everything else on this page is read-only) =====
  // One pending confirmation at a time; heavy deletes (group, active member,
  // reading turn) must have their name typed in the dialog first.
  del : any = null;
  deleting : boolean = false;

  get canDelete(){ return this.authService.isSuperAdmin(); }

  askDeleteGroup(){
    const g = this.group;
    if(!g || g.deleted_at){ return; }
    this.del = {
      title: 'Hapus grup tilawah ini?',
      subject: g.name,
      effects: [
        'Grup jadi tidak aktif: anggota tidak bisa chat, membuat tugas, atau menandai bacaan lagi.',
        'Riwayat chat dan progres tetap tersimpan dan masih bisa dilihat anggota.',
        `Semua ${this.formatNumber(g.member_count)} anggota mendapat pemberitahuan di aplikasi.`,
      ],
      confirmText: g.name,
      actionLabel: 'Hapus Grup',
      url: `tilawah-delete/${this.id}`,
      after: () => this.loadHeader(),
    };
  }
  askDeleteMember(m : any){
    if(m.is_owner || m.status == 2){ return; }
    const active = m.status == 1;
    this.del = {
      title: active ? 'Keluarkan anggota ini?' : (m.status == 3 ? 'Tolak permintaan bergabung?' : 'Batalkan undangan?'),
      subject: m.nameVw,
      effects: active
        ? ['Anggota langsung keluar dari grup dan tidak bisa membuka chat grup lagi.',
           'Riwayat bacaannya tetap tersimpan.',
           'Muncul info "dikeluarkan dari grup" di chat, dan anggota mendapat pemberitahuan.']
        : ['Undangan / permintaan ini dihapus; orang ini bisa diundang lagi nanti.'],
      confirmText: active ? m.nameVw : '',
      actionLabel: active ? 'Keluarkan' : 'Hapus',
      url: `tilawah-delete/${this.id}/members/${m.id}`,
      after: () => { this.loadMembers(); this.loadHeader(); },
    };
  }
  askDeleteTask(t : any, event? : any){
    if(event){ event.stopPropagation(); }
    const tag = this.turnTag(t.day);
    this.del = {
      title: 'Hapus tugas bacaan ini?',
      subject: `${tag} · ${t.range_text}`,
      effects: [
        `Bagian semua anggota di ${tag} ikut terhapus, termasuk yang sudah ditandai selesai.`,
        'Kartu tugasnya di chat (beserta balasannya) juga dihapus.',
        'Progres khatam dihitung ulang tanpa tugas ini.',
      ],
      confirmText: tag,
      actionLabel: 'Hapus Tugas',
      url: `tilawah-delete/${this.id}/tasks/${t.id}`,
      after: () => { this.loadTasks(); this.loadHeader(); this.loaded.progress = false; this.loaded.chats = false; },
    };
  }
  askDeleteAnnouncement(a : any){
    this.del = {
      title: 'Hapus pengumuman ini?',
      subject: a.text,
      effects: ['Pengumuman dan kartunya di chat dihapus untuk semua anggota.'],
      confirmText: '',
      actionLabel: 'Hapus',
      url: `tilawah-delete/${this.id}/announcements/${a.id}`,
      after: () => { this.loadAnnouncements(); this.loaded.chats = false; },
    };
  }
  askDeleteChat(c : any){
    if(c.isDeleted || !c.user_id){ return; }
    const preview = (c.text || '').trim() || (c.media && c.media.length ? '(media)' : '');
    this.del = {
      title: 'Hapus pesan ini?',
      subject: `${c.userName}: ${preview.length > 120 ? preview.slice(0, 120) + '…' : preview}`,
      effects: ['Isi dan medianya dihapus; anggota melihat "Pesan ini dihapus", sama seperti hapus pesan di aplikasi.'],
      confirmText: '',
      actionLabel: 'Hapus Pesan',
      url: `tilawah-delete/${this.id}/chats/${c.id}`,
      after: () => this.loadChats(),
    };
  }
  doDelete(){
    if(!this.del || this.deleting){ return; }
    const d = this.del;
    this.deleting = true;
    this.apiService.methodDeleteSilent(d.url, this.authService.getToken()).subscribe((response) => {
      this.deleting = false;
      if(response && response.status == 200){
        this.toastr.success(response.message || 'Berhasil dihapus');
        this.del = null;
        d.after();
      }else{
        this.toastr.error((response && response.message) || 'Gagal menghapus', 'Gagal');
      }
    }, () => { this.deleting = false; });
  }

  // "#Week 3" for this group's period; old rows / unmigrated DBs read as "#Day"
  turnTag(n : any, hash = true){
    return turnTag(this.group ? this.group.reading_period : 'day', n, hash);
  }

  // ----- conversation -----
  // first call loads the newest page and scrolls to the bottom; later calls
  // prepend older pages while keeping the viewport anchored on what was visible
  loadChats(older : boolean = false){
    if(older && (this.chatsOlderLoading || !this.chatsHasMore())){ return; }
    if(older){ this.chatsOlderLoading = true; } else { this.chatsLoading = true; }
    const offset = older ? this.chats.length : 0;
    const box : any = document.querySelector('.tc-scroll');
    const prevHeight = box ? box.scrollHeight : 0;
    this.apiService.methodGetSilent(`tilawah/${this.id}/chats/${offset}`, this.authService.getToken()).subscribe((response) => {
      this.chatsLoading = false; this.chatsOlderLoading = false;
      if(response && response.status == 200){
        const page = (response.data || []).map((c : any) => this.decorateChat(c)).reverse(); // oldest first
        this.chats = older ? page.concat(this.chats) : page;
        this.chatsTotal = response.total || 0;
        this.markDateSeparators();
        setTimeout(() => {
          const el : any = document.querySelector('.tc-scroll');
          if(!el){ return; }
          el.scrollTop = older ? el.scrollHeight - prevHeight : el.scrollHeight;
        });
      }
    }, () => { this.chatsLoading = false; this.chatsOlderLoading = false; });
  }
  chatsHasMore(){ return this.chats.length < this.chatsTotal; }
  chatsRemaining(){ return Math.max(0, this.chatsTotal - this.chats.length); }

  // Mirrors the mobile chat list (mobile/lib/presentation/pages/tilawah/group/chat/views/chat_list.dart):
  // bubble / deleted bubble / task card / announcement card / celebration /
  // khatam-reset / reassign cards / system chip — same shapes, same wording.
  private decorateChat(c : any){
    c.meta = this.kindMeta(c.kind);
    c.isDeleted = !!c.deleted_at;
    c.deletedVw = c.deleted_at ? moment(c.deleted_at).format('D MMM YYYY HH:mm') : '';
    c.isEdited = !!c.edited_at && !c.isDeleted;
    c.editedVw = c.edited_at ? moment(c.edited_at).format('D MMM YYYY HH:mm') : '';
    c.userPhotoUrl = this.photoUrl(c.user_photo, 'user/avatar/');
    // legacy rows predate tilawah_chat.user_id — the app shows them without an author line
    c.userName = c.user_name || (c.user_id ? 'Akun tidak ditemukan' : '');
    c.timeVw = c.created_at ? moment(c.created_at).format('HH:mm') : '';
    c.timeFull = c.created_at ? moment(c.created_at).format('D MMMM YYYY HH:mm') : '';
    c.dayKey = c.created_at ? moment(c.created_at).format('YYYY-MM-DD') : '';
    c.dayLabel = this.chatDayLabel(c.created_at);
    const text = String(c.text || '');
    c.media = c.isDeleted ? [] : this.mediaOf(c);
    c.hasPhotoOrVideo = !!(c.photo || c.video) && !c.isDeleted;
    // the app's placeholder captions ("Foto", "Video", "") are never shown next to the media itself
    c.showText = !c.isDeleted && !c.audio && !c.document && !!text.trim() && !(c.hasPhotoOrVideo && this.isMediaPlaceholderText(text));
    c.docName = c.document ? (text.trim() || this.fileName(c.document)) : '';

    // row shape, same precedence as the app's list builder
    if(c.isDeleted){ c.shape = 'deleted'; }
    else if(c.kind === 'task_created'){ c.shape = 'task'; }
    else if(c.kind === 'announcement'){ c.shape = 'announcement'; }
    else if(c.kind === 'khatam'){ c.shape = 'khatam'; }
    else if(c.kind === 'juz_complete'){ c.shape = 'juz'; }
    else if(c.kind === 'khatam_reset'){ c.shape = 'reset'; }
    else if(c.kind === 'task_reassigned'){ c.shape = 'reassign'; }
    else if(c.kind === 'message'){ c.shape = 'bubble'; }
    else { c.shape = 'chip'; }
    c.isBubble = c.shape === 'bubble' || c.shape === 'deleted';

    // task card text is authored as "#Day N\n<range>", announcement as "title\ndescription"
    const lines = text.split('\n');
    c.cardLabel = lines[0] || '';
    c.cardBody = lines.length > 1 ? lines.slice(1).join('\n') : '';
    if(c.shape === 'task' && !c.cardLabel && c.task_day){ c.cardLabel = this.turnTag(c.task_day); }
    if(c.shape === 'announcement'){
      c.cardLabel = c.announcement_title || c.cardLabel;
      c.cardBody = c.announcement_body || c.cardBody;
    }
    // chips and milestone cards: strip the leading emoji marker, bold the load-bearing words
    const body = text.replace(/^[^\p{L}\p{N}#@(]+/u, '').trim();
    c.chipHtml = this.emphasize(body, c.kind);
    c.chipTone = c.kind === 'task_done' ? 'green' : 'gray';
    c.chipIcon = {task_done: 'fa-check-circle', member_removed: 'fa-user-minus', group_deleted: 'fa-trash',
                  member_joined: 'fa-user-plus'}[c.kind] || 'fa-info-circle';

    // quote (reply) snippet, same rules as quoteSnippet() in the app
    if(c.reply_to_id){
      c.quoteAuthor = c.reply_user_name || '';
      if(c.reply_deleted_at){ c.quoteText = 'Pesan ini dihapus'; }
      else if(c.reply_text){ c.quoteText = c.reply_text; }
      else if(c.reply_has_media){ c.quoteText = '📎 Media'; }
      else { c.quoteText = 'Pesan tidak tersedia'; }
    }
    c.replyUserName = c.quoteAuthor; // kept for older template bindings
    return c;
  }

  // date chips: Hari ini / Kemarin / weekday (up to 5 days back) / full date — chatDayLabel() in the app
  private chatDayLabel(at : any){
    if(!at){ return ''; }
    const d = moment(at).startOf('day');
    const diff = moment().startOf('day').diff(d, 'days');
    if(diff === 0){ return 'Hari ini'; }
    if(diff === 1){ return 'Kemarin'; }
    if(diff >= 2 && diff <= 5){ return d.format('dddd'); }
    return d.format('D MMMM YYYY');
  }
  private isMediaPlaceholderText(text : string){
    return ['', 'Foto', 'Photo', 'Video'].includes(text.trim());
  }
  private escapeHtml(text : any){
    return String(text == null ? '' : text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  // HTML-escaped body with the same words bolded as the app's chips/cards
  private emphasize(body : string, kind : string){
    const esc = this.escapeHtml(body);
    const b = (t : string) => `<b>${this.escapeHtml(t)}</b>`;
    let m : any;
    if(kind === 'task_done' && (m = body.match(new RegExp(`^(.+?) (telah membaca|selesai) (${TURN_TAG_SOURCE})$`)))){
      return `${b(m[1])} ${this.escapeHtml(m[2])} ${b(m[3])}`;
    }
    if(kind === 'juz_complete' && (m = body.match(/^(.*?)(Juz \d+)(.*)$/))){
      return `${this.escapeHtml(m[1])}${b(m[2])}${this.escapeHtml(m[3])}`;
    }
    if(kind === 'khatam_reset' && (m = body.match(/^(.+?) memulai (Khatam ke-\d+)(.*)$/))){
      return `${b(m[1])} memulai ${b(m[2])}${this.escapeHtml(m[3])}`;
    }
    if(kind === 'task_reassigned' && (m = body.match(new RegExp(`^(.+?) melimpahkan bagian (.+?) di (${TURN_TAG_SOURCE}) ke (.+)$`)))){
      return `${b(m[1])} melimpahkan bagian ${b(m[2])} di ${b(m[3])} ke ${b(m[4])}`;
    }
    return esc;
  }

  // date chip above the first message of each day + Telegram-style runs:
  // consecutive bubbles from the same sender on the same day share tight
  // corners, the name shows on the first, the avatar on the last
  private markDateSeparators(){
    let lastDay = '';
    const rows = this.chats;
    rows.forEach((c : any, i : number) => {
      c.showDate = c.dayKey !== lastDay;
      lastDay = c.dayKey;
      const prev = i > 0 ? rows[i - 1] : null;
      const next = i < rows.length - 1 ? rows[i + 1] : null;
      const sameRun = (o : any) => o && o.isBubble && c.isBubble && o.user_id && o.user_id === c.user_id && o.dayKey === c.dayKey;
      c.runFirst = !sameRun(prev) || c.showDate;
      c.runLast = !sameRun(next) || (next && next.dayKey !== c.dayKey);
    });
  }
  // stored as full CDN URLs by the mobile API
  mediaOf(row : any){
    const out : any[] = [];
    if(row.photo){ out.push({type: 'photo', url: row.photo}); }
    if(row.video){ out.push({type: 'video', url: row.video}); }
    if(row.audio){ out.push({type: 'audio', url: row.audio}); }
    if(row.document){ out.push({type: 'document', url: row.document, name: this.fileName(row.document)}); }
    return out;
  }
  fileName(url : string){
    try { return decodeURIComponent(String(url).split('?')[0].split('/').pop() || 'Dokumen'); } catch(e){ return 'Dokumen'; }
  }
  fileExt(name : string){
    const m = String(name || '').match(/\.([a-z0-9]{1,5})$/i);
    return m ? m[1].toUpperCase() : 'FILE';
  }

  toggleThread(c : any){
    const t = this.threads[c.id] || (this.threads[c.id] = {open: false, loading: false, rows: null});
    t.open = !t.open;
    if(t.open && t.rows === null && !t.loading){
      t.loading = true;
      this.apiService.methodGetSilent(`tilawah/${this.id}/chats/${c.id}/replies`, this.authService.getToken()).subscribe((response) => {
        t.loading = false;
        if(response && response.status == 200){
          t.rows = (response.data || []).map((r : any) => {
            r.userPhotoUrl = this.photoUrl(r.user_photo, 'user/avatar/');
            r.userName = r.user_name || 'Akun tidak ditemukan';
            r.timeFull = r.created_at ? moment(r.created_at).format('D MMM YYYY HH:mm') : '';
            r.media = this.mediaOf(r);
            r.docName = r.document ? (String(r.text || '').trim() || this.fileName(r.document)) : '';
            r.showText = !r.audio && !r.document && !!String(r.text || '').trim() && !((r.photo || r.video) && this.isMediaPlaceholderText(String(r.text || '')));
            r.timeVw = r.created_at ? moment(r.created_at).format('HH:mm') : '';
            return r;
          });
        }else{
          t.rows = [];
        }
      }, () => { t.loading = false; t.rows = []; });
    }
  }
  thread(c : any){ return this.threads[c.id] || {open: false, loading: false, rows: null}; }

  // ----- announcements -----
  loadAnnouncements(){
    this.loading.announcements = true;
    const offset = (this.annPage - 1) * this.pageSize;
    this.apiService.methodGetSilent(`tilawah/${this.id}/announcements/${offset}`, this.authService.getToken()).subscribe((response) => {
      this.loading.announcements = false;
      if(response && response.status == 200){
        this.ann = (response.data || []).map((a : any) => {
          a.dateVw = a.created_at ? moment(a.created_at).format('D MMMM YYYY HH:mm') : '';
          a.readPct = a.active_members ? Math.round(a.read_count / a.active_members * 100) : 0;
          return a;
        });
        this.annTotal = response.total || 0;
        this.annPages = this.pagesFor(this.annPage, this.pagesOf(this.annTotal));
      }
    }, () => { this.loading.announcements = false; });
  }
  goAnnPage(p : any){
    if(p < 1 || p > this.pagesOf(this.annTotal) || p == this.annPage){ return; }
    this.annPage = p; this.loadAnnouncements();
  }

  // ----- khatam history -----
  loadKhatam(){
    this.loading.khatam = true;
    this.apiService.methodGetSilent(`tilawah/${this.id}/khatam-history`, this.authService.getToken()).subscribe((response) => {
      this.loading.khatam = false;
      if(response && response.status == 200){
        this.khatam = (response.data || []).map((c : any) => {
          c.startedVw = c.started_at ? moment(c.started_at).format('D MMM YYYY') : '-';
          c.completedVw = c.completed_at ? moment(c.completed_at).format('D MMM YYYY') : '-';
          c.durationVw = (c.started_at && c.completed_at) ? `${moment(c.completed_at).diff(moment(c.started_at), 'days') + 1} hari` : '';
          (c.members || []).forEach((m : any) => { m.photoUrl = this.photoUrl(m.photo, 'user/avatar/'); });
          return c;
        });
      }
    }, () => { this.loading.khatam = false; });
  }

  // ===== helpers =====
  pagesOf(total : number){ return Math.max(1, Math.ceil(total / this.pageSize)); }
  pagesFor(current : number, total : number){
    const pages: any[] = [];
    for(let p = 1; p <= total; p++){
      if(p == 1 || p == total || Math.abs(p - current) <= 1){
        pages.push(p);
      }else if(pages[pages.length - 1] != '...'){
        pages.push('...');
      }
    }
    return pages;
  }
  rangeFrom(page : number, total : number){ return total ? (page - 1) * this.pageSize + 1 : 0; }
  rangeTo(page : number, len : number){ return (page - 1) * this.pageSize + len; }
  formatNumber(value : any){ return this.formatService.dotFormat(value || 0); }

  // ===== lightbox =====
  openViewer(url : string){
    if(!url){ return; }
    this.isViewer = url;
    document.body.style.overflow = 'hidden';
  }
  closeViewer(){
    this.isViewer = '';
    document.body.style.overflow = '';
  }
  @HostListener('document:keydown.escape')
  onEscape(){ if(this.isViewer){ this.closeViewer(); } }
}
